import React, { FC, useMemo } from 'react'

// UTILITIES
import { flatten } from '@utils/string-helper'


interface BaseProps extends React.SVGProps<SVGSVGElement> {
    tint?: string
}

const defaultDimensions = { width: "1.4rem", height: "1.4rem" }
const defaultTint = "currentColor"

export const Plus: FC<BaseProps & { strokeWidth?: number, width?: string, height?: string }> = (props) => {
    const { tint = defaultTint, strokeWidth = 0.833, height = defaultDimensions.height, width = defaultDimensions.width, ...svgProps } = props

    return (
        <svg height={height} width={width} viewBox="0 0 16 19" {...svgProps}>
            <g clipPath="url(#a)">
                <path
                    fill={tint}
                    d="M9.078 3.063a1.124 1.124 0 1 0-2.25 0v5.062H1.766a1.124 1.124 0 1 0 0 2.25h5.062v5.063a1.124 1.124 0 1 0 2.25 0v-5.063h5.063a1.124 1.124 0 1 0 0-2.25H9.078V3.062Z"
                />
            </g>
            <defs>
                <clipPath id="a">
                    <path fill={tint} d="M.078.25h15.75v18H.078v-18Z" />
                </clipPath>
            </defs>
        </svg>
    )
}

export const Minus: FC<BaseProps & { strokeWidth?: number }> = (props) => {
    const { tint = defaultTint, strokeWidth = 0.833, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 29 2" {...svgProps}>
            <path
                d="M28 1H1"
                stroke={tint}
                strokeWidth={strokeWidth}
                fill="none"
                fillRule="evenodd"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    )
}

export const BikeSeat: FC<BaseProps & { type?: 'standard' | 'circle' }> = (props) => {
    const { tint = defaultTint, type, ...svgProps } = props

    const standard = (
        <svg {...defaultDimensions} viewBox="0 0 1200 627" {...svgProps}>
            <path
                d="M429.2 190.5c18.6 28.8 43.4 46.6 74.6 55.9-23-13.2-41.4-30.7-54-55.6 11.8 3 22.7 5.5 33.4 8.4 34.1 9.5 68.6 17 103.9 19.1 29.6 1.8 59.3 1.8 88.9 2.9 23.6.9 47.3 1.6 70.8 3.9 10.3 1 20.8 4.8 30.3 9.3 14.6 6.9 17.5 22.2 15 35.9-1 5.5-4.8 8.1-11.6 8-8.7 0-17.3-.6-26-.7-35.9-.6-71.7 1-106.4 11.5-10 3-19.9 6.8-29.2 11.6-29.6 15.3-60 17.4-90.8 5.2-31.1-12.3-61.8-25.7-90.1-43.8-8.8-5.6-17-12.9-24.1-20.7-9.4-10.4-9.1-21-.6-32 4.8-6.3 10.2-12.2 15.8-18.9ZM559.3 327.6c17.4 1.2 34.4-1.3 51.3-8 2.8 11.1 5.5 22 8.2 32.9 4.9 19.7 9.7 39.4 14.6 59.1 2.3 9.3-.7 14.2-10.1 16.5-8.7 2.2-17.4 4.4-26.2 6.5-6.5 1.5-12.3-1.7-13.9-8.2-8.1-32.4-16.1-64.9-24.1-97.4 0-.3 0-.6.2-1.4Z"
                fill={tint}
                strokeWidth={0}
            />
        </svg>
    )

    switch (type) {
        case 'standard': return standard
        case 'circle': return (
            <svg {...defaultDimensions} viewBox="0 0 45 45" {...props}>
                <title>{"Bike Seat Circle"}</title>
                <g fill="none" fillRule="evenodd">
                    <circle cx={22.5} cy={22.5} r={22.5} fill={tint} />
                    <g fill="#FFF" fillRule="nonzero">
                        <path d="M8.85 14c1.542 2.453 3.597 3.969 6.184 4.76-1.907-1.124-3.432-2.614-4.477-4.734.979.255 1.882.468 2.77.715 2.826.809 5.686 1.448 8.612 1.627 2.454.153 4.916.153 7.37.246 1.956.077 3.921.137 5.87.333.853.085 1.723.408 2.511.792 1.21.587 1.45 1.89 1.243 3.057-.082.468-.397.69-.961.681-.721 0-1.434-.05-2.155-.06-2.977-.05-5.944.086-8.82.98-.83.255-1.65.58-2.421.988-2.454 1.303-4.974 1.482-7.528.443-2.578-1.048-5.123-2.189-7.469-3.73-.73-.477-1.41-1.099-1.998-1.763-.779-.886-.754-1.789-.05-2.725.399-.537.846-1.04 1.31-1.61h.009ZM19.817 25.228a9.289 9.289 0 0 0 4.375-.728c.24 1.01.47 2.002.7 2.994.418 1.793.827 3.586 1.245 5.38.196.846-.06 1.292-.861 1.501-.742.2-1.484.4-2.235.592-.554.136-1.049-.155-1.186-.747-.69-2.948-1.373-5.906-2.055-8.864 0-.028 0-.055.017-.128Z" />
                    </g>
                </g>
            </svg>
        )
        default: return standard
    }
}

export const Close: FC<BaseProps> = (props) => {
    const { tint = defaultTint, strokeWidth = 1.042, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 15 20" {...svgProps}>
            <g
                stroke={tint}
                strokeWidth={strokeWidth}
                fill="none"
                fillRule="evenodd"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path
                    fill={tint}
                    d="M13.383 5.883a1.252 1.252 0 0 0-1.77-1.77L7.5 8.23 3.383 4.117a1.252 1.252 0 0 0-1.77 1.77L5.73 10l-4.113 4.117a1.252 1.252 0 0 0 1.77 1.77L7.5 11.77l4.117 4.113a1.252 1.252 0 0 0 1.77-1.77L9.27 10l4.113-4.117Z"
                />
            </g>
        </svg>
    )
}

export const NavigationMenu: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 28 16" {...svgProps}>
            <g
                stroke={tint}
                fill="none"
                fillRule="evenodd"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M27 .722H1M27 7.944H1M27 15.167H1" />
            </g>
        </svg>
    )
}

export const Search: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 26 26" {...svgProps}>
            <g
                stroke={tint}
                fill="none"
                fillRule="evenodd"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M1 9.348a8.348 8.348 0 1016.696 0A8.348 8.348 0 001 9.348zM15.25 15.25L25 25" />
            </g>
        </svg>
    )
}

export const Settings: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 18 19" {...svgProps}>
            <path
                fill={tint}
                d="M17.434 6.107a.796.796 0 0 1-.225.865l-1.522 1.385a6.815 6.815 0 0 1 0 1.786l1.522 1.385a.796.796 0 0 1 .225.865c-.155.418-.34.82-.555 1.206l-.166.285a8.956 8.956 0 0 1-.777 1.096.796.796 0 0 1-.86.24l-1.959-.623a6.766 6.766 0 0 1-1.547.893l-.44 2.008a.794.794 0 0 1-.64.625 9.08 9.08 0 0 1-2.987 0 .794.794 0 0 1-.64-.625l-.44-2.008a6.764 6.764 0 0 1-1.547-.893l-1.954.626a.802.802 0 0 1-.862-.239 8.946 8.946 0 0 1-.777-1.097l-.165-.285a8.716 8.716 0 0 1-.555-1.206.796.796 0 0 1 .225-.864l1.522-1.386a6.803 6.803 0 0 1 0-1.79L.788 6.973a.796.796 0 0 1-.225-.865c.154-.418.34-.82.555-1.206l.165-.285c.232-.386.493-.752.777-1.096a.796.796 0 0 1 .862-.24l1.958.623a6.765 6.765 0 0 1 1.547-.893l.44-2.008a.795.795 0 0 1 .639-.625 9.085 9.085 0 0 1 2.988-.004c.324.053.57.306.64.626l.44 2.007c.555.229 1.075.531 1.546.893l1.959-.622a.802.802 0 0 1 .861.239c.285.345.545.71.777 1.097l.165.285c.215.386.401.787.556 1.206l-.004.003ZM9 12.062a2.812 2.812 0 1 0 0-5.624 2.812 2.812 0 0 0 0 5.625Z"
            />
        </svg>
    )
}

export const Bell: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 22 26" {...svgProps}>
            <g
                stroke={tint}
                strokeWidth={1.042}
                fill="none"
                fillRule="evenodd"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M2.563 12.457a8.333 8.333 0 1116.666 0v6.771c0 .863.7 1.563 1.563 1.563H1c.863 0 1.563-.7 1.563-1.563v-6.77zM10.896 1v3.125M13.448 22.874a2.604 2.604 0 01-5.104 0M13.5 7.737a4.688 4.688 0 012.604 4.2" />
            </g>
        </svg>
    )
}

export const Trash: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 26 26" {...svgProps}>
            <g
                stroke={tint}
                strokeWidth={1.042}
                fill="none"
                fillRule="evenodd"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M22.354 5.167l-1.887 17.926a2.083 2.083 0 01-2.071 1.865H7.562a2.083 2.083 0 01-2.071-1.865L3.604 5.167M1 5.167h23.958M8.292 5.167V2.042C8.292 1.466 8.758 1 9.333 1h7.292c.575 0 1.042.466 1.042 1.042v3.125M12.979 9.854v10.938M17.667 9.854l-.521 10.938M8.292 9.854l.521 10.938" />
            </g>
        </svg>
    )
}

export const LocationPin: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 21 30" {...svgProps}>
            <g
                stroke={tint}
                strokeWidth={1.042}
                fill="none"
                fillRule="evenodd"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M10.5 1c5.247 0 9.5 4.164 9.5 9.3 0 4.399-6.967 15.353-8.967 18.414A.637.637 0 0110.5 29a.637.637 0 01-.533-.286C7.967 25.654 1 14.7 1 10.3 1 5.164 5.253 1 10.5 1z" />
                <path d="M7 9.5a3.5 3.5 0 107 0 3.5 3.5 0 00-7 0z" />
            </g>
        </svg>
    )
}

export const Phone: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 28 28" {...svgProps}>
            <path
                d="M10.556 8.563a2.736 2.736 0 000-3.866l-2.9-2.896a2.733 2.733 0 00-3.865 0L2.2 3.39a4.093 4.093 0 00-.519 5.16A63.557 63.557 0 0019.45 26.317a4.1 4.1 0 005.16-.516l1.589-1.59a2.733 2.733 0 000-3.867l-2.9-2.899a2.732 2.732 0 00-3.865 0l-.966.967a64.858 64.858 0 01-8.878-8.88l.966-.97z"
                stroke={tint}
                strokeWidth={1.042}
                fill={tint}
                fillRule="evenodd"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    )
}

export const Lock: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 13 14" {...svgProps}>
            <path
                fill={tint}
                d="M3.938 3.938V5.25h4.374V3.937a2.187 2.187 0 1 0-4.374 0ZM2.188 5.25V3.937a3.939 3.939 0 0 1 7.874 0V5.25h.438c.965 0 1.75.785 1.75 1.75v5.25c0 .965-.785 1.75-1.75 1.75H1.75C.785 14 0 13.215 0 12.25V7c0-.965.785-1.75 1.75-1.75h.438Z"
            />
        </svg>
    )
}

export const Email: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 28 20" {...svgProps}>
            <g
                stroke='#fff'
                strokeWidth={1.042}
                fill={tint}
                fillRule="evenodd"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M1 1h26v17.875H1z" />
                <path d="M27 1l-13 9.75L1 1" />
            </g>
        </svg>
    )
}

export const Share: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 18 18" {...svgProps}>
            <path
                fill="none"
                stroke={tint}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M13.5 6a2.25 2.25 0 1 0 0-4.5 2.25 2.25 0 0 0 0 4.5ZM4.5 11.25a2.25 2.25 0 1 0 0-4.5 2.25 2.25 0 0 0 0 4.5ZM13.5 16.5a2.25 2.25 0 1 0 0-4.5 2.25 2.25 0 0 0 0 4.5ZM6.442 10.133l5.123 2.985M11.557 4.883 6.442 7.868"
            />
        </svg>
    )
}

export const QRCode: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 112 112" {...svgProps}>
            <path
                fill={tint}
                d="M44.336 30.333v14h-14v-14h14Zm7-7h-28v28h28v-28Zm-7 44.334v14h-14v-14h14Zm7-7h-28v28h28v-28Zm30.333-30.334v14h-14v-14h14Zm7-7h-28v28h28v-28Zm-28 37.334h7v7h-7v-7Zm7 7h7v7h-7v-7Zm7-7h7v7h-7v-7Zm-14 14h7v7h-7v-7Zm7 7h7v7h-7v-7Zm7-7h7v7h-7v-7Zm7-7h7v7h-7v-7Zm0 14h7v7h-7v-7Zm16.334-49A4.68 4.68 0 0 1 93.336 28v-9.333h-9.333A4.68 4.68 0 0 1 79.336 14a4.68 4.68 0 0 1 4.667-4.667h14A4.68 4.68 0 0 1 102.669 14v14a4.68 4.68 0 0 1-4.666 4.667ZM102.669 98V84a4.68 4.68 0 0 0-4.666-4.667A4.68 4.68 0 0 0 93.336 84v9.333h-9.333A4.68 4.68 0 0 0 79.336 98a4.68 4.68 0 0 0 4.667 4.667h14A4.68 4.68 0 0 0 102.669 98Zm-88.666 4.667h14A4.68 4.68 0 0 0 32.669 98a4.68 4.68 0 0 0-4.666-4.667h-9.334V84a4.68 4.68 0 0 0-4.666-4.667A4.68 4.68 0 0 0 9.336 84v14a4.68 4.68 0 0 0 4.667 4.667ZM9.336 14v14a4.68 4.68 0 0 0 4.667 4.667A4.68 4.68 0 0 0 18.669 28v-9.333h9.334A4.68 4.68 0 0 0 32.669 14a4.68 4.68 0 0 0-4.666-4.667h-14A4.68 4.68 0 0 0 9.336 14Z"
            />
        </svg>
    )
}

export const Chat: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 26 26" {...svgProps}>
            <g
                stroke={tint}
                strokeWidth={1.042}
                fill="none"
                fillRule="evenodd"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M10.375 17.667c0 4.466 5.208 7.812 9.896 5.729l4.166 1.562-1.562-3.125a5.202 5.202 0 002.083-4.166c0-3.286-3.264-6.25-7.291-6.25-4.027 0-7.292 2.964-7.292 6.25z" />
                <path d="M18.604 9.333c.065-.343.1-.692.104-1.041C18.708 4.265 14.75 1 9.854 1 4.958 1 1 4.265 1 8.292a6.702 6.702 0 002.622 5.177L1.52 17.146l5.145-2.059a10.41 10.41 0 001.626.377" />
            </g>
        </svg>
    )
}

export const Heart: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 28 25" {...svgProps}>
            <path
                d="M14 23.941L2.92 12.286C.928 10.288.43 7.2 1.693 4.656h0c.961-1.907 2.752-3.232 4.825-3.571a6.514 6.514 0 015.674 1.853l1.81 1.685 1.808-1.685a6.515 6.515 0 015.675-1.852c2.072.339 3.863 1.664 4.825 3.57h0c1.261 2.545.764 5.632-1.23 7.63L14 23.94z"
                stroke={tint}
                strokeWidth={1.042}
                fill="none"
                fillRule="evenodd"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    )
}

export const View: FC<BaseProps & { state?: "on" | "off" }> = (props) => {
    const { tint = defaultTint, state = "on", ...svgProps } = props

    return state === "on" ? (
        <svg {...defaultDimensions} viewBox="0 0 25 15" {...svgProps}>
            <g transform="translate(1 1)" stroke={tint} strokeWidth={2} fill="none" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round">
                <circle cx={11.498} cy={6.5} r={3.5} />
                <path d="M22.878 6.17C21.708 4.85 17.058 0 11.498 0 5.938 0 1.288 4.85.118 6.17a.52.52 0 000 .66C1.288 8.15 5.938 13 11.498 13c5.56 0 10.21-4.85 11.38-6.17a.52.52 0 000-.66z" />
            </g>
        </svg>
    ) : (
        <svg {...defaultDimensions} viewBox="0 0 25 21" {...svgProps}>
            <g stroke={tint} strokeWidth={2} fill="none" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.02 7.04a21.88 21.88 0 014 3.71s-5.17 6.5-11.52 6.5a10.33 10.33 0 01-3-.5M7.34 15.91A21.45 21.45 0 011 10.75s5.15-6.5 11.5-6.5a11.49 11.49 0 015.16 1.34M16.5 10.75a4 4 0 01-4 4M22.25 1L2.75 20.5M9.67 13.58a4.002 4.002 0 015.66-5.66" />
            </g>
        </svg>
    )
}

export const House: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 26 26" {...svgProps}>
            <path
                d="M24.707 12.293l-11-11a1 1 0 00-1.414 0l-11 11A1 1 0 002 14h1.25a.25.25 0 01.25.25V24a1 1 0 001 1h5.75a.25.25 0 00.25-.25V20a2.5 2.5 0 115 0v4.75c0 .138.112.25.25.25h5.75a1 1 0 001-1v-9.75a.25.25 0 01.25-.25H24a1 1 0 00.707-1.707z"
                fillRule="nonzero"
                stroke={tint}
                fill={tint}
            />
        </svg>
    )
}

export const Store: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 20 16" {...svgProps}>
            <path
                fill={tint}
                d="M1.15 6h17.7a1.15 1.15 0 0 0 .956-1.787L17.444.669A1.493 1.493 0 0 0 16.197 0H3.803c-.5 0-.969.25-1.247.669L.194 4.209A1.152 1.152 0 0 0 1.15 6ZM2 7v7.5A1.5 1.5 0 0 0 3.5 16h7a1.5 1.5 0 0 0 1.5-1.5V7h-2v5H4V7H2Zm14 0v8c0 .553.447 1 1 1"
            />
        </svg>
    )
}

export const GiftBox: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 18 18" {...svgProps}>
            <path
                fill={tint}
                d="M6.697 2.419 7.921 4.5H5.344a1.406 1.406 0 1 1 0-2.813h.077c.524 0 1.013.278 1.276.732Zm-4.447.675c0 .506.123.984.337 1.406H1.125C.503 4.5 0 5.003 0 5.625v2.25C0 8.497.503 9 1.125 9h15.75C17.497 9 18 8.497 18 7.875v-2.25c0-.622-.503-1.125-1.125-1.125h-1.463A3.096 3.096 0 0 0 12.656 0h-.077a3.172 3.172 0 0 0-2.732 1.56L9 3.007l-.847-1.442A3.168 3.168 0 0 0 5.42 0h-.077A3.094 3.094 0 0 0 2.25 3.094Zm11.813 0c0 .777-.63 1.406-1.407 1.406H10.08l1.224-2.081a1.482 1.482 0 0 1 1.276-.732h.077c.777 0 1.406.63 1.406 1.407ZM1.124 10.125v6.188c0 .931.756 1.687 1.688 1.687h5.062v-7.875h-6.75Zm9 7.875h5.063c.931 0 1.687-.756 1.687-1.688v-6.187h-6.75V18Z"
            />
        </svg>
    )
}

export const ShoppingCart: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 26 26" {...svgProps}>
            <g
                stroke={tint}
                strokeWidth={1.042}
                fill="none"
                fillRule="evenodd"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M4 6h19.93c.334 0 .648.158.85.429.203.27.27.62.182.949l-2.195 8.21C22.387 17.012 21.119 18 19.672 18H6.477" />
                <path d="M1 1h.398c.995 0 1.855.697 2.06 1.672l3.506 16.656A2.105 2.105 0 009.024 21H21M7 23.5a1.5 1.5 0 103 0 1.5 1.5 0 00-3 0h0zM18 23.5a1.5 1.5 0 103 0 1.5 1.5 0 00-3 0h0z" />
            </g>
        </svg>
    )
}

export const Calendar: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 25 25" {...svgProps}>
            <g
                transform="translate(1 1)"
                stroke={tint}
                fill="none"
                fillRule="evenodd"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <rect y={2} width={23} height={21} rx={1} />
                <path d="M5 0v5M18 0v5M0 7h23" />
            </g>
        </svg>
    )
}

export const Pencil: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 26 26" {...svgProps}>
            <g
                stroke={tint}
                strokeWidth={1.042}
                fill="none"
                fillRule="evenodd"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M7.735 23.378L1 24.958l1.58-6.734L19.192 1.61a2.083 2.083 0 012.947 0l2.21 2.21a2.083 2.083 0 010 2.947L7.735 23.378zM23.615 7.505l-5.16-5.158M22.128 8.992L16.981 3.82M7.998 23.118l-5.156-5.156" />
            </g>
        </svg>
    )
}

export const Edit: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 14 14" {...svgProps}>
            <path
                fill={tint}
                d="M12.895.45a1.53 1.53 0 0 0-2.165 0l-.823.82 2.677 2.676.823-.823a1.53 1.53 0 0 0 0-2.165l-.512-.509Zm-8.18 6.015a1.51 1.51 0 0 0-.37.599l-.81 2.428a.66.66 0 0 0 .16.672.651.651 0 0 0 .672.159l2.428-.81a1.55 1.55 0 0 0 .599-.369l4.574-4.577-2.68-2.68-4.574 4.578Zm-2.09-4.86A2.626 2.626 0 0 0 0 4.232v7a2.626 2.626 0 0 0 2.625 2.625h7a2.626 2.626 0 0 0 2.625-2.625V8.606a.874.874 0 1 0-1.75 0v2.625a.874.874 0 0 1-.875.875h-7a.874.874 0 0 1-.875-.875v-7c0-.484.391-.875.875-.875H5.25a.874.874 0 1 0 0-1.75H2.625Z"
            />
        </svg>
    )
}

export const Leave: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 -2 10 18" {...svgProps}>
            <g
                stroke={tint}
                strokeWidth={1.042}
                fill="none"
                fillRule="evenodd"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M7.583 2.625V.583A.583.583 0 0 0 7 0H.583A.583.583 0 0 0 0 .583v12.25c0 .322.261.584.583.584H7a.583.583 0 0 0 .583-.584v-2.041M13.417 6.708H2.333M10.5 9.625l2.917-2.917L10.5 3.792" />
            </g>
        </svg>
    )
}

export const LogOut: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 26 26" {...svgProps}>
            <g
                stroke={tint}
                strokeWidth={1.042}
                fill="none"
                fillRule="evenodd"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M1.952 17.668a11.98 11.98 0 100-9.375M13.5 17.147l4.167-4.167L13.5 8.814M17.667 12.98H1" />
            </g>
        </svg>
    )
}

export const Clock: FC<BaseProps & { type?: 'default' | 'rush' | 'value' }> = (props) => {
    const { tint = defaultTint, type = 'default', ...svgProps } = props

    switch (type) {
        case 'default': return (
            <svg {...defaultDimensions} viewBox="0 0 25 25" {...svgProps}>
                <g stroke={tint} fill="none" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12.5a11.5 11.5 0 1023 0 11.5 11.5 0 00-23 0z" />
                    <path d="M12.501 7v5.5l6 5.5" />
                </g>
            </svg>
        )
        case 'rush': return (
            <svg {...defaultDimensions} viewBox="0 0 53 29" {...svgProps}>
                <g stroke={tint} fill="none" fillRule="evenodd">
                    <path d="M38.709 28c-7.404.062-13.532-6.18-13.312-14.003C25.592 7.069 31.34.963 38.727 1 46.14 1.038 51.994 7.004 52 14.595c.006 7.403-5.94 13.4-13.291 13.405ZM10.455 16.749c-2.695 0-5.39.004-8.085-.003-.943-.002-1.39-.399-1.37-1.18.015-.55.4-.968.976-1.05.136-.019.277-.015.416-.015 5.355 0 10.71-.002 16.065 0 .985 0 1.55.577 1.351 1.379-.118.477-.42.794-.958.839-.328.027-.659.028-.989.029-2.469.002-4.937.001-7.406 0ZM13.74 5.498c-1.603 0-3.204.007-4.806-.004-.645-.005-1.109-.336-1.242-.835-.138-.515.092-1 .592-1.25.243-.121.506-.157.778-.157 3.131 0 6.263-.006 9.394.003.859.002 1.398.461 1.392 1.131-.007.67-.555 1.108-1.412 1.11-1.565.006-3.131.002-4.697.002ZM16.003 23.503c.821 0 1.642-.007 2.464.002.83.008 1.38.457 1.38 1.11s-.558 1.125-1.37 1.129c-1.68.007-3.36.009-5.041 0-.812-.005-1.359-.484-1.349-1.144.01-.635.548-1.085 1.338-1.095.86-.01 1.719-.002 2.578-.002Z" />
                    <path d="M36.488 13.36c0-.712-.006-1.425.001-2.137.008-.728.539-1.206 1.342-1.223.802-.016 1.39.438 1.41 1.17.033 1.193.022 2.387.007 3.58-.004.287.09.497.35.697.651.501 1.285 1.019 1.91 1.542.662.554.682 1.246.072 1.714-.563.433-1.38.391-2.021-.117-.833-.66-1.648-1.335-2.486-1.991-.425-.333-.614-.714-.593-1.196.03-.68.007-1.36.008-2.039Z" />
                </g>
            </svg>
        )
        case 'value': return (
            <svg {...defaultDimensions} viewBox="0 0 39 33" {...props}>
                <g fillRule="nonzero" fill="none">
                    <path d="M32 15.859C31.667 7.597 24.858 1 16.506 1 7.946 1 1 7.942 1 16.5 1 25.02 7.871 31.926 16.383 32" stroke={tint} strokeWidth={2} strokeLinecap="round" />
                    <path stroke={tint} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" d="M17 7v10H8" />
                    <g fill={tint}>
                        <path d="M27.796 28.777V31h-9.248v-1.769l4.713-4.299c1.083-.994 1.274-1.596 1.274-2.174 0-.92-.662-1.461-1.962-1.461-1.044 0-1.936.393-2.535 1.179L18 21.186C18.943 19.86 20.662 19 22.854 19c2.726 0 4.547 1.351 4.547 3.488 0 1.143-.33 2.187-2.05 3.722l-2.816 2.567h5.261ZM40 28.519h-1.987V31h-2.765v-2.481h-6.586v-1.842l5.771-7.48h2.968l-5.35 7.099h3.287v-2.211h2.675v2.21H40v2.224z" />
                    </g>
                </g>
            </svg>
        )
    }

}

export const Group: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox='0 -4 27 27' {...svgProps}>
            <g stroke={defaultTint} strokeWidth={1.042} fill="none" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round" >
                <path d="M3.385 4.948a4.427 4.427 0 1 0 8.855 0 4.427 4.427 0 0 0-8.855 0ZM.52 18.23a7.292 7.292 0 0 1 14.584 0H.521ZM17.187 18.23h7.292a5.73 5.73 0 0 0-8.361-5.09M15.306 7.552a3.385 3.385 0 1 0 6.771 0 3.385 3.385 0 0 0-6.77 0h0Z" />
            </g>
        </svg>
    )
}

export const GroupOfThree: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox='0 0 37 26' {...svgProps}>
            <g fill={tint}>
                <path d="M25.43 24.7c-5.127 1.931-9.343 1.51-13.867.04-.348-.252-.76-.401-1.112-.65a3.552 3.552 0 0 1-1.497-2.681c-.262-3.899 1.032-8.377 5.299-9.205 2.361 1.833 6.1 1.875 8.47.039 2.395.401 4.07 2.242 4.808 4.506.47 1.44.8 4.325.267 5.732-.424 1.121-1.313 1.76-2.367 2.218v.001Z" />
                <path d="M18.254.006c4.48-.199 7.38 4.724 5.133 8.664-2.247 3.939-7.66 3.753-9.808-.05-2.114-3.74.459-8.427 4.675-8.614ZM29.613 19.456c-.107-3.13-1.48-6.39-4.254-7.962-.418-.237-.865-.407-1.299-.61a4.272 4.272 0 0 1 2.665-1.642c.1.004.481.317.615.39 1.502.835 3.594.847 5.1.018.293-.161.44-.417.791-.36 2.29.369 3.605 3.016 3.738 5.152.156 2.51-.22 3.514-2.605 4.372-1.502.54-3.164.674-4.751.642ZM12.953 10.908c-3.563 1.148-5.494 4.92-5.553 8.548-1.638.03-3.355-.106-4.898-.691-2.39-.907-2.629-1.98-2.459-4.472.14-2.052 1.503-4.657 3.7-4.992.398-.062.628.267.975.448 1.301.676 3.389.678 4.696.016.176-.09.76-.524.874-.523 1.045.22 2.035.782 2.665 1.666Z" />
                <path d="M6.56.204c2.985-.31 5.351 2.305 4.635 5.292C10.322 9.13 5.466 9.9 3.53 6.706c-1.577-2.6.01-6.19 3.03-6.502ZM29.359.204c4.47-.461 6.509 5.327 2.9 7.782-3.36 2.285-7.632-1.03-6.362-4.92.487-1.49 1.907-2.701 3.462-2.861V.204Z" />
            </g>
        </svg>
    )
}

export const GroupLocked: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox='0 0 38 34' {...svgProps}>
            <g stroke={tint} fill="none" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round" >
                <path fill={tint} d="M19.607 20.78c-2.487.935-.003 5.225-2.203 5.22-2.432-.005-3.28-.434-5.707-1.22-.355-.256-.777-.409-1.135-.663a3.62 3.62 0 0 1-1.529-2.733c-.268-3.974 1.054-8.54 5.41-9.384 2.41 1.869 6.228 1.911 8.647.04.442.073 2.348.247 1.818.465-4.949 2.035-4.076 8.004-4.216 8.034-.382.08-.406-.054-1.085.242v-.001ZM18.266.006c4.346-.19 7.16 4.521 4.98 8.29-2.1 3.633-7.43 3.593-9.514-.047-2.05-3.578.445-8.064 4.534-8.243ZM26.284 11.845c-.078.013-.124-.096-.188-.144a7.125 7.125 0 0 0-.792-.505c-.42-.23-.869-.397-1.304-.594A4.312 4.312 0 0 1 26.677 9c.1.004.483.309.618.381 1.509.813 3.611.825 5.124.017.294-.158.441-.407.795-.35 2.3.358 3.622 2.94 3.755 5.023.136 2.13-.131 3.148-1.78 3.928-.247.116-.239-7.53-8.904-6.155v.001Z" />
                <path fill={tint} d="M13 10.63c-3.575 1.123-5.514 4.815-5.573 8.366-1.644.03-3.367-.104-4.916-.677-2.4-.886-2.638-1.937-2.468-4.376.14-2.009 1.509-4.558 3.713-4.886.4-.06.63.262.98.439 1.305.662 3.4.664 4.712.016.177-.088.763-.513.877-.512 1.05.215 2.044.765 2.675 1.63ZM6.47.023c2.839-.289 5.089 2.161 4.407 4.96-.829 3.406-5.446 4.128-7.288 1.134-1.5-2.436.01-5.8 2.882-6.094ZM29.497.024c4.235-.43 6.168 4.98 2.748 7.276-3.184 2.136-7.231-.964-6.028-4.6.461-1.393 1.807-2.525 3.28-2.675ZM34.465 21.857h1.107c.748 0 1.484.84 1.483 1.566v9.01c-.088.883-.675 1.496-1.576 1.567H20.687c-.751-.03-1.396-.517-1.562-1.252-.135-3.111-.03-6.247-.055-9.367.005-.67.741-1.523 1.43-1.523h1.155c-.03-2.239-.168-4.27 1.186-6.178 2.527-3.564 7.869-3.574 10.418-.026 1.372 1.911 1.236 3.955 1.205 6.205l.001-.002Zm-2.59 0c-.063-1.534.264-3.118-.574-4.497-1.45-2.388-5.028-2.392-6.48 0-.838 1.379-.512 2.962-.575 4.497h7.63Zm-2.213 9.012-.657-2.923c1.345-1.163.09-3.267-1.57-2.577-.586.244-.928.89-.882 1.51.031.429.277.767.564 1.067l-.657 2.923h3.202Z" />            </g>
        </svg>
    )
}

export const Info: FC<BaseProps & { type?: 'default' | 'filled' }> = (props) => {
    const { tint = defaultTint, type = 'default', ...svgProps } = props

    switch (type) {
        case 'default': return (
            <svg {...defaultDimensions} viewBox="0 0 26 26" {...svgProps}>
                <g stroke={tint} fill="none" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1.096 13.096c0 6.628 5.373 12 12 12s12-5.372 12-12c0-6.627-5.373-12-12-12s-12 5.373-12 12z" />
                    <path d="M15.823 18.555h-1.636a1.09 1.09 0 01-1.091-1.09v-7.092a.545.545 0 00-.545-.545h-1.637M12.84 7.106a.284.284 0 100 .569.284.284 0 000-.569" />
                </g>
            </svg>
        )
        case 'filled': return (
            <svg {...defaultDimensions} viewBox="0 0 19 19" {...svgProps}>
                <path fill={tint} fillRule="nonzero" d="M9.5 0a9.5 9.5 0 1 0 0 19 9.5 9.5 0 0 0 0-19Zm.198 3.958a1.188 1.188 0 1 1 0 2.375 1.188 1.188 0 0 1 0-2.375Zm1.781 10.688H8.313a.792.792 0 1 1 0-1.584h.593c.11 0 .198-.088.198-.197V9.302a.198.198 0 0 0-.198-.198h-.594a.792.792 0 1 1 0-1.583h.792a1.583 1.583 0 0 1 1.584 1.583v3.76c0 .11.088.198.197.198h.594a.792.792 0 0 1 0 1.584Z" />
            </svg>
        )
    }
}

export const Shield: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 60 61" {...svgProps}>
            <g>
                <path
                    fill={tint}
                    d="M30 .25c.54 0 1.078.117 1.57.34l22.067 9.363c2.578 1.09 4.5 3.633 4.488 6.703-.059 11.625-4.84 32.895-25.031 42.563a7.159 7.159 0 0 1-6.188 0C6.715 49.55 1.934 28.28 1.876 16.656c-.013-3.07 1.91-5.613 4.487-6.703L28.441.59a3.73 3.73 0 0 1 1.56-.34Zm0 7.828v44.297c16.172-7.828 20.52-25.16 20.625-35.555L30 8.078Z"
                />
            </g>
        </svg>
    )
}

export const Warning: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 23 23" {...svgProps}>
            <g
                stroke={tint}
                fill="none"
                fillRule="evenodd"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M22.213 21.58a.569.569 0 01-.553.894h-20a.569.569 0 01-.553-.894L11.213 1.369c.246-.492.648-.492.894 0L22.213 21.58zM11.66 15.978v-7" />
                <path d="M11.651 17.978a.246.246 0 00-.24.255.255.255 0 00.253.245h.005a.246.246 0 00.241-.255.255.255 0 00-.25-.245h-.005" />
            </g>
        </svg>
    )
}

export const WarningSmall: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 25 24" {...svgProps}>
            <path
                fill={tint}
                d="M12.5 1.5c.666 0 1.28.352 1.617.928l10.125 17.25c.342.581.342 1.299.01 1.88a1.878 1.878 0 0 1-1.627.942H2.375c-.67 0-1.294-.36-1.627-.942a1.883 1.883 0 0 1 .01-1.88l10.125-17.25A1.871 1.871 0 0 1 12.5 1.5Zm0 6c-.623 0-1.125.502-1.125 1.125v5.25c0 .623.502 1.125 1.125 1.125s1.125-.502 1.125-1.125v-5.25c0-.623-.502-1.125-1.125-1.125ZM14 18a1.5 1.5 0 1 0-3 0 1.5 1.5 0 0 0 3 0Z"
            />
        </svg>
    )
}

export const Pending: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox='0 0 35 35' {...svgProps}>
            <g id="Organizer" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                <g id="Activated-Copy-11" transform="translate(-22, -109)">
                    <g id="Group-10" transform="translate(0, 91)">
                        <g id="Icons/check-mark" transform="translate(20.5, 20.5)">
                            <g id="Time-Lapse--Streamline-Core" transform="translate(1.5, -2.5)">
                                <path d="M17.5,3.75 C9.906075,3.75 3.75,9.906075 3.75,17.5 C3.75,25.094 9.906075,31.25 17.5,31.25 C21.9967,31.25 25.98925,29.0935 28.501,25.75025 C29.123,24.92235 30.29825,24.75545 31.12625,25.3775 C31.95425,25.9995 32.121,27.175 31.499,28.00275 C28.3095,32.24825 23.2258,35 17.5,35 C7.835025,35 0,27.165 0,17.5 C0,7.835025 7.835025,0 17.5,0 C18.451025,0 19.3856,0.0760345 20.2977,0.22270775 C21.3201,0.38712 22.015625,1.34922 21.851225,2.37162 C21.6868,3.394025 20.7247,4.08955 19.7023,3.92515 C18.98635,3.81 18.25085,3.75 17.5,3.75 Z" id="Vector-(Stroke)" fill="#4DF085"></path>
                                <path d="M25.37725,3.873725 C25.99925,3.045825 27.1745,2.878925 28.0025,3.50095 C29.32625,4.495525 30.504,5.673425 31.49875,6.9972 C32.12075,7.8251 31.95375,9.0005 31.126,9.622525 C30.298,10.24455 29.1225,10.07765 28.5005,9.249725 C27.71825,8.20835 26.79125,7.28145 25.75,6.49905 C24.922,5.877025 24.7551,4.701625 25.37725,3.873725 L25.37725,3.873725 Z M32.62825,13.148775 C33.65075,12.984375 34.61275,13.6799 34.77725,14.7023 C34.92375,15.6144 35,16.548975 35,17.5 C35,18.451 34.92375,19.3856 34.77725,20.2977 C34.61275,21.3201 33.65075,22.015625 32.62825,21.851225 C31.606,21.6868 30.91025,20.7247 31.07475,19.7023 C31.19,18.98635 31.25,18.25085 31.25,17.5 C31.25,16.74915 31.19,16.01365 31.07475,15.2977 C30.91025,14.2753 31.606,13.3132 32.62825,13.148775 Z M19.375,8.75 C19.375,7.714475 18.535525,6.875 17.5,6.875 C16.464475,6.875 15.625,7.714475 15.625,8.75 L15.625,18.75 C15.625,19.408625 15.97055,20.01895 16.535325,20.3578 L22.785325,24.1078 C23.673275,24.640575 24.825025,24.35265 25.35775,23.464675 C25.8905,22.576725 25.60275,21.424975 24.714675,20.8922 L19.375,17.6884 L19.375,8.75 Z" id="Union" fill="#11B84B"></path>
                            </g>
                        </g>
                    </g>
                </g>
            </g>
        </svg>
    )
}

export const Check: FC<BaseProps & { type?: 'default' | 'bold' | 'success' | 'bordered' }> = (props) => {
    const { tint = defaultTint, type = 'default', ...svgProps } = props

    function getComponent() {
        switch (type) {
            case 'default':
                return (
                    <svg {...defaultDimensions} viewBox="0 0 20 20" {...svgProps}>
                        <path d="M18.625 1.374L6.25 18.624 1.375 13.75" stroke={tint} fill="none" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                )
            case 'bold':
                return (
                    <svg {...defaultDimensions} viewBox="0 0 24 24" {...svgProps}>
                        <path d="M20.625 3.374L8.25 20.624 3.375 15.75" stroke={tint} strokeWidth={6} fill="none" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                )
            case 'success':
                return (
                    <svg {...defaultDimensions} viewBox="0 0 34 34" {...svgProps}>
                        <g fill="none" fillRule="evenodd">
                            <circle fill={tint !== 'currentColor' ? tint : "var(--splitcard-blue-500)"} cx={17} cy={17} r={17} />
                            <path stroke="var(--splitcard-white)" strokeWidth={6} strokeLinecap="round" strokeLinejoin="round" d="M25.625 8.374L13.25 25.624 8.375 20.75" />
                        </g>
                    </svg>
                )
            case 'bordered':
                return (
                    <svg {...defaultDimensions} viewBox="0 0 19 19" {...svgProps}>
                        <g stroke={tint} fill="none" fillRule="evenodd">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m14.25 5-6.636 9.25L5 11.636" />
                            <circle cx={9.5} cy={9.5} r={9} />
                        </g>
                    </svg>
                )
        }
    }

    return getComponent()
}

export const CheckSmall: FC<BaseProps & { type?: 'default' | 'bordered' }> = (props) => {
    const { tint = defaultTint, type = 'default', ...svgProps } = props

    function getComponent() {
        switch (type) {
            case 'default':
                return (
                    <svg {...defaultDimensions} viewBox="0 0 17 12" {...svgProps}>
                        <path
                            fill={tint}
                            d="M15.794.955c.44.44.44 1.154 0 1.593l-9 9c-.439.44-1.153.44-1.592 0l-4.5-4.5a1.127 1.127 0 0 1 1.592-1.593L6 9.157 14.205.955c.44-.439 1.153-.439 1.593 0h-.004Z"
                        />
                    </svg>
                )
            case 'bordered':
                return (
                    <svg {...defaultDimensions} viewBox="0 0 17 16" {...svgProps}>
                        <path
                            fill={tint}
                            d="M8.664 16a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.531-9.469-4 4a.747.747 0 0 1-1.06 0l-2-2a.75.75 0 0 1 1.06-1.06l1.47 1.47 3.468-3.472a.75.75 0 0 1 1.06 1.06l.002.002Z"
                        />
                    </svg>
                )
        }
    }

    return getComponent()
}

export const HyperLink: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 25 25" {...svgProps}>
            <g
                stroke={tint}
                fill="none"
                fillRule="evenodd"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <path d="M12.97 18.606l-4.227 4.227a3.987 3.987 0 01-5.637 0l-.94-.94a3.985 3.985 0 010-5.634l6.107-6.107a3.987 3.987 0 015.637 0l.939.94" />
                <path d="M12.03 6.394l4.227-4.226a3.985 3.985 0 015.637 0l.939.938a3.985 3.985 0 010 5.635l-6.106 6.107a3.985 3.985 0 01-5.637 0l-.94-.94" />
            </g>
        </svg>
    )
}

export const CreditCard: FC<BaseProps & { type?: 'remove' | 'check' | 'sync' | 'disable' | 'simple' }> = (props) => {
    const { tint = defaultTint, type = "remove", ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox={type === "simple" ? "0 0 16 13" : "0 0 25 25"} {...svgProps}>
            {type === "remove" && (
                <g stroke={tint} fill="none" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M24 18a6 6 0 11-12 0 6 6 0 0112 0zM20.12 15.88l-4.24 4.24M20.12 20.12l-4.24-4.24M20 5H1" />
                    <path d="M10 14H2.5A1.5 1.5 0 011 12.5v-10A1.5 1.5 0 012.5 1h16A1.5 1.5 0 0120 2.5V10M18 8h-3M6 10H3M8 8H3" />
                </g>
            )}
            {type === "check" && (
                <g stroke={tint} fill="none" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round">
                    <g transform="translate(12 12)">
                        <circle cx={6} cy={6} r={6} />
                        <path d="M8.67 4.26l-2.9 3.87a.75.75 0 01-1.13.08l-1.5-1.5" />
                    </g>
                    <path d="M20 5H1M10.5 14h-8A1.5 1.5 0 011 12.5v-10A1.5 1.5 0 012.5 1h16A1.5 1.5 0 0120 2.5V10M18 8h-3M6 10H3M8 8H3" />
                </g>
            )}
            {type === "sync" && (
                <g stroke={tint} fill="none" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19.59 15.64L22.63 17 24 13.93M11.82 15.76A5.57 5.57 0 0122.63 17M14.88 20.36L11.84 19l-1.37 3.07M22.65 20.23A5.57 5.57 0 0111.84 19M20 5H1" />
                    <path d="M10 14H2.5A1.5 1.5 0 011 12.5v-10A1.5 1.5 0 012.5 1h16A1.5 1.5 0 0120 2.5V10M18 8h-3M6 10H3M8 8H3" />
                </g>
            )}
            {type == "disable" && (
                <g stroke={tint} fill="none" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round">
                    <g transform="translate(12 12)">
                        <circle cx={6} cy={6} r={6} />
                        <path d="M1.76 10.24l8.48-8.48" />
                    </g>
                    <path d="M20 5H1M10 14H2.5A1.5 1.5 0 011 12.5v-10A1.5 1.5 0 012.5 1h16A1.5 1.5 0 0120 2.5V10M18 8h-3M6 10H3M8 8H3" />
                </g>
            )}
            {type == "simple" && (
                <path
                    fill={tint}
                    d="M1.75 0C.785 0 0 .785 0 1.75v.875h15.75V1.75C15.75.785 14.965 0 14 0H1.75Zm14 5.25H0v5.25c0 .965.785 1.75 1.75 1.75H14c.965 0 1.75-.785 1.75-1.75V5.25ZM3.062 8.75h1.75c.241 0 .438.197.438.438"
                />
            )}
        </svg>
    )
}

export const Develop: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 26 21" {...svgProps}>
            <g transform="translate(1 1)" stroke={tint} strokeWidth={1.042} fill="none" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15.885 9.375L19.01 12.5l-3.125 3.125M8.073 9.375L4.948 12.5l3.125 3.125M13.542 9.375l-3.125 6.25" />
                <rect width={23.958} height={18.75} rx={2} />
                <path d="M0 5.208h23.958M3.646 2.344a.26.26 0 110 .52.26.26 0 010-.52M6.77 2.344a.26.26 0 110 .52.26.26 0 010-.52M9.896 2.344a.26.26 0 110 .52.26.26 0 010-.52" />
            </g>
        </svg>
    )
}

export const Analytics: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 26 20" {...svgProps}>
            <g transform="translate(1 1)" stroke={tint} strokeWidth={1.042} fill="none" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round">
                <rect width={23.958} height={17.708} rx={1} />
                <path d="M4.162 12.5l2.679-4.464a.52.52 0 01.893 0l2.306 3.839a.52.52 0 00.846.066l1.735-2.084a.52.52 0 01.8 0l1.724 2.069a.52.52 0 00.852-.075l3.79-6.643" />
            </g>
        </svg>
    )
}

export const Ellipsis: FC<BaseProps & { animated?: boolean }> = (props) => {
    const { tint = defaultTint, animated, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 26 7" {...svgProps}>
            <g fill={tint} fillRule="nonzero">
                <circle cx={3.385} cy={3.385} r={3.385}>
                    {animated && <animate id="dot-a"
                        repeatCount="indefinite"
                        attributeName="cy"
                        values="0;5;0"
                        dur="1s"
                    />}
                </circle>
                <circle cx={12.5} cy={3.385} r={3.385}>
                    {animated && <animate id="dot-b"
                        begin="dot-a.begin + 125ms"
                        repeatCount="indefinite"
                        attributeName="cy"
                        values="0;5;0"
                        dur="1s"
                    />}
                </circle>
                <circle cx={21.615} cy={3.385} r={3.385}>
                    {animated && <animate id="dot-c"
                        begin="dot-b.begin + 250ms"
                        repeatCount="indefinite"
                        attributeName="cy"
                        values="0;5;0"
                        dur="1s"
                    />}
                </circle>
            </g>
        </svg>
    )
}

export const Order: FC<BaseProps & { type?: 'shipped' | 'delivered' }> = (props) => {
    const { tint = defaultTint, type = 'shipped', ...svgProps } = props

    function getComponent() {
        switch (type) {
            case 'shipped':
                return (
                    <svg {...defaultDimensions} viewBox="0 0 23 23" {...svgProps}>
                        <g stroke={tint} strokeWidth={1.042} fill="none" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M7.25 16.625H2.042A1.042 1.042 0 0 1 1 15.583V5.598c0-.276.11-.541.305-.737l3.556-3.556c.196-.195.46-.305.737-.305h5.387c.277 0 .542.11.737.305l3.556 3.556c.195.196.305.46.305.737V7.25M8.292 1v4.167M17.669 10.375l4.687 3.646-4.687 3.646" />
                            <path d="M22.356 14.02H18.71a6.25 6.25 0 0 0-6.25 6.25v2.084M15.49 5.167H1.094" />
                        </g>
                    </svg>
                )
            case 'delivered':
                return (
                    <svg {...defaultDimensions} viewBox="0 0 26 26" {...svgProps}>
                        <g stroke={tint} strokeWidth={1.042} fill="none" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12.458 18.708a6.25 6.25 0 1 0 12.5 0 6.25 6.25 0 0 0-12.5 0Z" />
                            <path d="m21.494 16.892-3.026 4.034a.783.783 0 0 1-1.178.084l-1.563-1.562M9.333 16.625H2.042A1.042 1.042 0 0 1 1 15.583V5.598c0-.276.11-.541.305-.737l3.556-3.556c.196-.195.46-.305.737-.305h5.387c.277 0 .542.11.737.305l3.556 3.556c.195.196.305.46.305.737v3.735M15.49 5.167H1.094M8.292 1v4.167" />
                        </g>
                    </svg>
                )
        }
    }

    return getComponent()
}

export const Refund: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 25 25" {...svgProps}>
            <g stroke={tint} strokeWidth={1.042} fill="none" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12.458c0 6.329 5.13 11.459 11.458 11.459 6.329 0 11.459-5.13 11.459-11.459C23.917 6.13 18.787 1 12.458 1 6.13 1 1 6.13 1 12.458Z" />
                <path d="M6.208 10.377h9.375a3.125 3.125 0 1 1 0 6.25h-1.562" />
                <path d="m9.333 13.5-3.125-3.125L9.333 7.25" />
            </g>
        </svg>
    )
}

export const Refresh: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 25 25" {...svgProps}>
            <g stroke={tint} strokeWidth={1.042} fill="none" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5.167 13.499v4.687H1" />
                <path d="M5.167 18.187a9.896 9.896 0 1 1 2.573 1.708" />
            </g>
        </svg>
    )
}

export const Hamburglar: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox='0 0 28 16' {...svgProps}>
            <g stroke={tint} fill="none" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round">
                <path d="M27 .722H1M27 7.944H1M27 15.167H1" />
            </g>
        </svg>
    )
}

export const HandGift: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg width={196} height={192} xmlns="http://www.w3.org/2000/svg" {...svgProps}>
            <g transform="translate(2 3)" stroke={tint} strokeWidth={4} fill="none" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round">
                <rect y={112.367} width={33.333} height={66.667} rx={0.5} />
                <path d="M33.333 166.534c87.734 29.25 60.834 30.083 156.025-18.042a4.258 4.258 0 0 0 .7-7.116 20.708 20.708 0 0 0-20.141-4.009l-43.35 14.409" />
                <path d="M75 154.034h45.833a8.333 8.333 0 1 0 0-16.667h-25a54.917 54.917 0 0 0-37.5-16.666h-25M83.333 54.034v45.833a4.167 4.167 0 0 0 4.167 4.167h75a4.167 4.167 0 0 0 4.167-4.167V54.034" />
                <rect x={75} y={29.034} width={100} height={25} rx={0.5} />
                <path d="M125 104.034v-75M148.7 17.25a65.983 65.983 0 0 1-23.567 11.784 65.767 65.767 0 0 1 11.784-23.567c7.325-7.325 11.475-6.191 14.75-2.95 3.275 3.242 4.358 7.409-2.967 14.734ZM101.3 17.25a65.983 65.983 0 0 0 23.567 11.784 65.767 65.767 0 0 0-11.784-23.567c-7.325-7.325-11.475-6.191-14.75-2.95-3.275 3.242-4.358 7.409 2.967 14.734Z" />
            </g>
        </svg>
    )
}

export const Star: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox='0 0 28 16' {...svgProps}>
            <title>Star</title>
            <path d="M10.606.338a.52.52 0 0 1 .975 0l2.637 7.474h7.446a.52.52 0 0 1 .338.917l-6.222 5.159 2.604 7.822a.52.52 0 0 1-.802.585l-6.49-4.762L4.6 22.295a.52.52 0 0 1-.799-.585l2.604-7.823L.182 8.73a.52.52 0 0 1 .339-.916h7.447L10.606.338Z" fill="var(--splitcard-yellow-500)" fillRule="evenodd" />
        </svg>
    )
}

export const Ticket: FC<BaseProps & { type?: 'standard' | 'circle' }> = (props) => {
    const { tint = defaultTint, type = 'standard', ...svgProps } = props

    const standard = (
        <svg {...defaultDimensions} viewBox="0 0 21 14" {...svgProps}>
            <title>Ticket</title>
            <path fill={tint} fillRule="nonzero" d="M20.3 5.714a.875.875 0 0 0 .7-.875V1.312C21 .589 20.412 0 19.687 0H1.313C.588 0 0 .588 0 1.313v3.543a.875.875 0 0 0 .7.875 1.312 1.312 0 0 1 0 2.573.875.875 0 0 0-.7.875v3.508A1.313 1.313 0 0 0 1.313 14h18.374c.725 0 1.313-.588 1.313-1.313V9.145a.875.875 0 0 0-.7-.875 1.312 1.312 0 0 1 0-2.573v.018ZM4.594 12.469a.656.656 0 1 1 .656-.656.665.665 0 0 1-.656.656Zm0-3.211a.656.656 0 1 1 0-1.313.656.656 0 0 1 0 1.313Zm0-3.203a.656.656 0 1 1 .656-.656.665.665 0 0 1-.656.656Zm0-3.211a.656.656 0 1 1 .656-.656.665.665 0 0 1-.656.656Zm10.28 7.875a1.531 1.531 0 1 1 0-3.063 1.531 1.531 0 0 1 0 3.063Zm1.313-6.563H8.313a.656.656 0 0 1 0-1.312h7.874a.656.656 0 0 1 0 1.312Z" />
        </svg>
    )

    switch (type) {
        case 'standard': return standard
        case 'circle': return (
            <svg {...defaultDimensions} viewBox="0 0 45 45" {...svgProps}>
                <title>Ticket Circled</title>
                <g fill="none" fillRule="evenodd">
                    <circle cx={22.5} cy={22.5} r={22.5} fill={tint} />
                    <path fill="var(--splitcard-white)" fillRule="nonzero" d="M33.2 20.53a1 1 0 0 0 .8-1V15.5a1.5 1.5 0 0 0-1.5-1.5h-21a1.5 1.5 0 0 0-1.5 1.5v4.05a1 1 0 0 0 .8 1 1.5 1.5 0 0 1 0 2.94 1 1 0 0 0-.8 1v4.01a1.5 1.5 0 0 0 1.5 1.5h21a1.5 1.5 0 0 0 1.5-1.5v-4.05a1 1 0 0 0-.8-1 1.5 1.5 0 0 1 0-2.94v.02Zm-17.95 7.72a.75.75 0 1 1 .75-.75.76.76 0 0 1-.75.75Zm0-3.67a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Zm0-3.66a.75.75 0 1 1 .75-.75.76.76 0 0 1-.75.75Zm0-3.67a.75.75 0 1 1 .75-.75.76.76 0 0 1-.75.75Zm11.75 9a1.75 1.75 0 1 1 0-3.5 1.75 1.75 0 0 1 0 3.5Zm1.5-7.5h-9a.75.75 0 1 1 0-1.5h9a.75.75 0 0 1 0 1.5Z" />
                </g>
            </svg>
        )
        default: return standard
    }
}

export const Arrow: FC<BaseProps & { facing?: 'up' | 'right' | 'down' | 'left' }> = (props) => {
    const { tint = defaultTint, facing = 'up', ...rest } = props

    const rotation = useMemo(() => {
        switch (facing) {
            case 'up': return '90deg'
            case 'right': return '180deg'
            case 'down': return '270deg'
            case 'left': return '0deg'
        }
    }, [facing])

    const svgProps: React.SVGProps<SVGSVGElement> = {
        ...rest,
        style: { ...rest.style, transform: `rotate(${rotation})` },
        stroke: rest.stroke || tint,
        fill: rest.fill || 'none',
        strokeWidth: rest.strokeWidth || '1px',
        strokeLinecap: rest.strokeLinecap || 'round',
    }

    return (
        <svg {...defaultDimensions} viewBox="0 0 18 16" {...svgProps}>
            <title>{`${facing.charAt(0).toUpperCase()}${facing.slice(1)}`} Arrow</title>
            <path d="M 1 8 H 17 M 1 8 L 8 1 M 1 8 L 8 15" />
        </svg>
    )
}

export const Receipt: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox='0 0 16 26' {...svgProps}>
            <title>Receipt</title>
            <g fill="none" fillRule="evenodd" stroke={defaultTint} strokeWidth={1.042}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.625 23.825a.52.52 0 0 1-.86.396l-2.786-2.388-3.854 2.89a.52.52 0 0 1-.625 0l-3.854-2.89-2.787 2.388A.52.52 0 0 1 1 23.825V2.042C1 1.466 1.466 1 2.042 1h13.541c.576 0 1.042.466 1.042 1.042v21.783h0ZM3.604 6.208h4.167M3.604 9.333h5.729M3.604 12.458h6.25M3.604 15.583h4.688" />
                <path d="M13.5 15.844a.26.26 0 1 1 0-.521M13.5 15.844a.26.26 0 1 0 0-.521M13.5 12.719a.26.26 0 1 1 0-.521M13.5 12.719a.26.26 0 1 0 0-.521M13.5 9.594a.26.26 0 1 1 0-.521M13.5 9.594a.26.26 0 0 0 0-.521M13.5 6.469a.26.26 0 1 1 0-.521M13.5 6.469a.26.26 0 0 0 0-.521" />
            </g>
        </svg>
    )
}

export const AddUser: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox='0 0 14 14' {...svgProps}>
            <title>AddUser</title>
            <g fill="none" fillRule="evenodd" stroke={defaultTint} strokeWidth={1.042}>
                <path
                    stroke={tint}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.167}
                    d="M9.333 12.25v-1.167A2.333 2.333 0 0 0 7 8.75H3.5a2.333 2.333 0 0 0-2.333 2.333v1.167M5.25 6.417a2.333 2.333 0 1 0 0-4.667 2.333 2.333 0 0 0 0 4.667ZM11.083 4.667v3.5M12.833 6.417h-3.5"
                />
            </g>
        </svg>
    )
}

export const RemoveUser: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox='0 0 14 14' {...svgProps}>
            <title>AddUser</title>
            <g fill="none" fillRule="evenodd" stroke={defaultTint} strokeWidth={1.042}>
                <path
                    stroke={tint}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.167}
                    d="M9.333 12.25v-1.167A2.333 2.333 0 0 0 7 8.75H3.5a2.333 2.333 0 0 0-2.333 2.333v1.167M5.25 6.417a2.333 2.333 0 1 0 0-4.667 2.333 2.333 0 0 0 0 4.667ZM12.833 6.417h-3.5"
                />
            </g>
        </svg>
    )
}

export const UserIcon: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox='0 0 27 27' {...svgProps}>
            <title>User Icon</title>
            <g fill="none" fillRule="evenodd" stroke={defaultTint} strokeLinecap="round" strokeLinejoin="round" transform="translate(1 1)">
                <circle cx={12.5} cy={12.5} r={12.5} />
                <path d="M3.76 21.436a27.815 27.815 0 0 1 5.883-2.557c.91-.336 1.01-2.422.357-3.14-.941-1.036-1.74-2.25-1.74-5.18a4.143 4.143 0 0 1 4.24-4.53 4.143 4.143 0 0 1 4.24 4.53c0 2.934-.799 4.144-1.74 5.18-.652.718-.553 2.804.357 3.14a27.815 27.815 0 0 1 5.883 2.557" />
            </g>
        </svg>
    )
}

export const Splitcard: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox='0 0 28 20' {...svgProps}>
            <title>splitcard</title>
            <g fill="none" fillRule="evenodd">
                <rect width={27} height={19} x={0.5} y={0.5} stroke={tint} rx={3} />
                <g fill={tint} fillRule="nonzero">
                    <path d="M10.289 18c-.03-.022-.065-.02-.098-.027a.952.952 0 0 1-.697-.792c-.023-.204.03-.387.11-.57l2.292-5.326L14.082 6.2c.531-1.236 1.062-2.473 1.595-3.71a.77.77 0 0 1 .637-.482c.018-.007.038-.002.057-.003.028-.003.057.006.084-.006h.047c.031.021.07.017.104.025a.95.95 0 0 1 .701.807.979.979 0 0 1-.08.485L15.09 8.285c-.666 1.548-1.33 3.095-1.997 4.643l-1.836 4.268c-.047.11-.092.222-.143.332a.774.774 0 0 1-.626.463c-.019.007-.038.002-.058.003-.027.003-.056-.006-.083.006h-.059Z" />
                    <path d="M16.327 2.019c-.03.046-.065.017-.097.021-.003-.006-.004-.01-.003-.015l.004-.006h.096ZM10.182 18.045c.03-.046.065-.017.097-.02.003.005.004.01.002.014-.001.004-.002.006-.004.006h-.095Z" />
                    <path d="M16.327 2.022c0 .007-.002.014-.003.02h-.097l.001-.02h.099ZM10.182 18.043l.003-.021h.097l-.001.02h-.099ZM17.555 14.087c-.678 0-1.252-.127-1.798-.385a4.09 4.09 0 0 1-.982-.653 4.162 4.162 0 0 1-.645-.745c-.023-.033-.024-.058-.008-.095l.49-1.117.377-.855.49-1.122.376-.85.494-1.128.325-.74a.092.092 0 0 1 .072-.057c.323-.066.65-.084.978-.068a3.357 3.357 0 0 1 1.88.668c.245.183.464.395.66.63.147.175.117.389-.067.533-.08.062-.147.138-.222.206-.16.148-.311.305-.473.451-.078.07-.144.153-.235.209a.269.269 0 0 1-.257.011.572.572 0 0 1-.194-.138 1.785 1.785 0 0 0-.767-.483 1.65 1.65 0 0 0-1.187.064c-.584.255-.944.702-1.096 1.312-.108.43-.058.85.12 1.256.095.217.229.408.398.574.263.257.576.423.94.483.406.066.8.023 1.17-.171.147-.078.28-.176.402-.287.097-.087.189-.18.29-.261.15-.121.32-.107.466.02.18.158.368.306.545.465.112.101.242.183.335.305.099.128.105.27.018.404-.178.273-.386.52-.63.737-.26.232-.554.412-.875.55-.28.12-.57.194-.87.238-.197.03-.395.045-.52.04v-.001ZM9.568 5.912a4.509 4.509 0 0 1 1.56.248c.25.089.494.195.704.368.122.1.193.294.11.467-.146.307-.293.613-.43.924a.322.322 0 0 1-.362.175c-.127-.03-.24-.1-.36-.144a4.238 4.238 0 0 0-.899-.247c-.19-.025-.38-.04-.568.014-.141.04-.256.117-.304.274a.41.41 0 0 0 .15.445c.131.103.28.169.43.235.374.165.756.31 1.122.498.212.109.42.226.616.363.033.023.038.04.021.08l-.448 1.079-.38.927-.359.86-.383.933c-.04.097-.082.194-.121.292-.011.028-.03.03-.053.03a5.014 5.014 0 0 1-.65-.033 4.505 4.505 0 0 1-.675-.13 3.29 3.29 0 0 1-1.017-.465c-.097-.067-.181-.144-.206-.273a.28.28 0 0 1 .025-.179l.457-.963c.022-.046.038-.096.071-.136.076-.092.217-.112.334-.05.204.11.414.201.632.275.259.087.522.153.795.176.177.015.35.018.522-.042.304-.108.36-.466.198-.698-.09-.13-.218-.211-.348-.288-.267-.158-.553-.271-.836-.392a4.39 4.39 0 0 1-1.056-.61 2.268 2.268 0 0 1-.674-.859 2.24 2.24 0 0 1-.163-.6 2.606 2.606 0 0 1-.018-.515c.02-.31.098-.604.246-.876.097-.18.218-.34.361-.48a2.2 2.2 0 0 1 .72-.462 2.92 2.92 0 0 1 .677-.18c.2-.03.401-.048.56-.04l-.001-.001Z" />
                </g>
            </g>
        </svg>
    )
}

export const PhysicalCard: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox='0 0 30 20' {...svgProps}>
            <title>physicalcard</title>
            <g fill="none" fillRule="evenodd">
                <path
                    stroke={tint}
                    d="M25 4v12.72c0 .706-.503 1.28-1.123 1.28H1.117C.503 18 0 17.426 0 16.72V4h25Z"
                />
                <path
                    fill={tint}
                    d="M25 1.492V5H0V1.492C0 .672.504 0 1.116 0h22.76C24.498 0 25 .672 25 1.492"
                />
                <path
                    stroke={tint}
                    d="M25 1.492V5H0V1.492C0 .672.504 0 1.116 0h22.76C24.498 0 25 .672 25 1.492Z"
                />
                <path
                    fill={tint}
                    d="M24.5 16a4.5 4.5 0 1 1 .001-9 4.5 4.5 0 0 1-.001 9Z"
                />
                <path
                    stroke={tint}
                    strokeWidth="inherit"
                    d="M29 11.5a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0Z"
                />
                <path
                    fill="#3D99F5"
                    stroke="#3D99F5"
                    strokeWidth="inherit"
                    d="m26.446 10.477-.556.562a.653.653 0 0 1-.785.109l.314-.316-.256-.257-.315.316a.66.66 0 0 1 .108-.787l.56-.559a.65.65 0 0 1 .93 0c.127.124.192.29.192.465a.645.645 0 0 1-.192.467m-2.4 2.41-.562.559a.657.657 0 0 1-.929 0 .66.66 0 0 1 0-.932l.557-.562a.663.663 0 0 1 .466-.192c.113 0 .221.03.319.087l-.31.312.252.254.315-.312a.658.658 0 0 1-.109.787m2.658-3.6a1.047 1.047 0 0 0-1.443 0l-.556.558c-.196.192-.3.45-.3.726 0 .21.064.412.184.583l-.427.424c-.394-.265-.962-.225-1.302.116l-.56.562a1.025 1.025 0 0 0 0 1.446c.198.2.459.297.722.297.26 0 .521-.098.72-.297l.56-.562c.355-.355.395-.91.116-1.308l.423-.424a1.012 1.012 0 0 0 1.306-.116l.557-.558A1.02 1.02 0 0 0 27 10.01c0-.272-.105-.53-.297-.721"
                />
                <path stroke={tint} d="M4 12.5h14M4 14.5h6" />
            </g>
        </svg>
    )
}

const faces = { up: '90deg', right: '0deg', down: '270deg', left: '180deg' }
export const Chevron: FC<BaseProps & { facing?: 'up' | 'right' | 'down' | 'left', disabled?: boolean }> = (props) => {
    const { className: classProps, style: styleProps, tint = defaultTint, facing = 'left', disabled, ...svgProps } = props
    const className = flatten({ disabled }, 'chevron', facing, classProps)
    const deg = faces[facing]

    return (
        <svg className={className} {...defaultDimensions} viewBox="0 0 8 14" strokeWidth={2} style={{ transform: `rotate(${deg})`, transition: 'transform 0.6s ease', ...styleProps }} {...svgProps}>
            <path
                fill={tint}
                d="M7.706 7.708c.39-.39.39-1.025 0-1.416l-6-6A1.002 1.002 0 0 0 .291 1.708l5.293 5.294-5.29 5.293a1.001 1.001 0 0 0 1.415 1.416l6-6-.003-.003Z"
            />
        </svg>
    )
}

export const Triangle: FC<BaseProps & { facing?: 'down' }> = (props) => {
    const { tint = defaultTint, facing = "down", ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 23 19" {...svgProps}>
            <title>Triangle</title>
            <path fill={tint} fillRule="evenodd" d="M22.97 0 11.5 19 .03 0z" />
        </svg>
    )
}

export const UnknownPM: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 102 72" strokeWidth={2.042} {...svgProps}>
            <title>Unknown Payment Method</title>
            <g fill="none" fillRule="evenodd" stroke={tint} strokeLinecap="round" strokeLinejoin="round" strokeWidth="inherit" transform="translate(2 2)">
                <rect width={98} height={68} rx={10} />
                <path d="M0 18h98M85 34H72M47 34H13M26 46H13" />
            </g>
        </svg>
    )
}

export const USD: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 11 22" strokeWidth={2.042} {...svgProps}>
            <title>USD</title>
            <path fill={tint} fillRule="nonzero" d="M11 14.758c.01-1.8-1.054-3.424-2.684-4.097L3.754 8.828a1.677 1.677 0 0 1-.974-1.854 1.64 1.64 0 0 1 1.573-1.355h4.473c.741 0 1.342-.615 1.342-1.375s-.6-1.375-1.342-1.375h-1.79a.226.226 0 0 1-.223-.229V1.375C6.813.615 6.213 0 5.471 0 4.731 0 4.13.616 4.13 1.375v1.256c0 .12-.089.22-.205.229C1.944 3.04.34 4.586.047 6.601c-.293 2.015.8 3.974 2.642 4.738l4.563 1.833a1.677 1.677 0 0 1 .973 1.854 1.64 1.64 0 0 1-1.573 1.355H2.18c-.74 0-1.342.615-1.342 1.375s.601 1.375 1.342 1.375h1.79c.123 0 .223.102.223.229v1.265c0 .76.6 1.375 1.342 1.375.741 0 1.342-.616 1.342-1.375v-1.256c0-.12.09-.22.206-.229 2.214-.204 3.913-2.103 3.918-4.382Z" />
        </svg>
    )
}

export const Gas: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 18 18" {...svgProps}>
            <path
                fill={tint}
                d="M1.125 2.25A2.252 2.252 0 0 1 3.375 0H9a2.252 2.252 0 0 1 2.25 2.25V9h.281a3.094 3.094 0 0 1 3.094 3.094v1.125c0 .467.376.844.844.844a.842.842 0 0 0 .844-.844V7.805a2.252 2.252 0 0 1-1.688-2.18v-2.25L13.5 2.25a.798.798 0 0 1 0-1.125.798.798 0 0 1 1.125 0l2.718 2.718c.422.421.657.995.657 1.592V13.22a2.53 2.53 0 0 1-2.531 2.531 2.53 2.53 0 0 1-2.531-2.531v-1.125c0-.777-.63-1.406-1.407-1.406h-.281v5.062c.622 0 1.125.503 1.125 1.125S11.872 18 11.25 18H1.125a1.124 1.124 0 1 1 0-2.25V2.25Zm2.25.563v3.374c0 .31.253.563.563.563h4.5c.309 0 .562-.253.562-.563V2.813a.564.564 0 0 0-.563-.562h-4.5a.564.564 0 0 0-.562.563Z"
            />
        </svg>
    )
}

export const Medicine: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 21 18" {...svgProps}>
            <path
                fill={tint}
                d="M3.938 3.375c-.932 0-1.688.756-1.688 1.688V9h3.375V5.062c0-.931-.756-1.687-1.688-1.687ZM0 5.063a3.936 3.936 0 0 1 3.938-3.938 3.936 3.936 0 0 1 3.937 3.938v7.875a3.935 3.935 0 0 1-3.938 3.937A3.935 3.935 0 0 1 0 12.937V5.064Zm19.508 8.978c-.25.433-.833.461-1.188.11l-6.595-6.596c-.352-.351-.327-.939.109-1.188a5.623 5.623 0 0 1 7.675 7.675Zm-2.092 2.092a5.623 5.623 0 0 1-7.675-7.675c.25-.432.834-.46 1.19-.108l6.594 6.595c.352.351.327.939-.109 1.188Z"
            />
        </svg>
    )
}

export const MoneyTransfer: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 23 18" {...svgProps}>
            <path
                fill={tint}
                d="M18.809 1.442A.843.843 0 0 1 20 .25l2.25 2.25a.843.843 0 0 1 .245.597c0 .225-.087.44-.246.598L20 5.945a.843.843 0 0 1-1.192-1.192l.81-.808-6.118-.007a.842.842 0 0 1-.844-.844c0-.468.376-.844.844-.844h6.12l-.811-.808ZM3.69 13.254l-.808.809H9c.468 0 .844.376.844.843A.842.842 0 0 1 9 15.75H2.88l.808.809a.843.843 0 0 1-1.192 1.192l-2.25-2.247A.843.843 0 0 1 0 14.906c0-.225.088-.44.246-.597l2.25-2.25a.843.843 0 0 1 1.192 1.192l.003.003ZM3.375 2.25h8.504a1.829 1.829 0 0 0 1.62 2.672h4.128a1.827 1.827 0 0 0 3.07 1.716l.678-.679V13.5a2.252 2.252 0 0 1-2.25 2.25h-8.504A1.829 1.829 0 0 0 9 13.078H4.872a1.827 1.827 0 0 0-3.07-1.716l-.678.68V4.5a2.252 2.252 0 0 1 2.25-2.25Zm2.25 2.25h-2.25v2.25a2.252 2.252 0 0 0 2.25-2.25Zm13.5 6.75a2.252 2.252 0 0 0-2.25 2.25h2.25v-2.25Zm-7.875 1.125a3.375 3.375 0 1 0 0-6.75 3.375 3.375 0 0 0 0 6.75Z"
            />
        </svg>
    )
}

export const Gambling: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 23 18" {...svgProps}>
            <path
                fill={tint}
                d="M9.664 1.206a2.531 2.531 0 0 0-3.579 0l-4.88 4.88a2.531 2.531 0 0 0 0 3.578l4.88 4.88a2.531 2.531 0 0 0 3.58 0l4.879-4.88a2.531 2.531 0 0 0 0-3.579l-4.88-4.88ZM7.031 7.875a.844.844 0 1 1 1.688 0 .844.844 0 0 1-1.688 0ZM3.375 7.03a.844.844 0 1 1 0 1.688.844.844 0 0 1 0-1.688Zm4.5 6.188a.844.844 0 1 1 0-1.687.844.844 0 0 1 0 1.687Zm4.5-6.188a.844.844 0 1 1 0 1.687.844.844 0 0 1 0-1.687Zm-4.5-2.812a.844.844 0 1 1 0-1.688.844.844 0 0 1 0 1.688Zm3.375 11.53A2.252 2.252 0 0 0 13.5 18h6.75a2.252 2.252 0 0 0 2.25-2.25V9a2.252 2.252 0 0 0-2.25-2.25h-4.018a3.655 3.655 0 0 1-.893 3.71l-4.09 4.089v1.202Zm5.625-4.218a.843.843 0 1 1 0 1.687.843.843 0 0 1 0-1.687Z"
            />
        </svg>
    )
}

export const AdultSites: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 23 18" {...svgProps}>
            <path
                fill={tint}
                d="M1.364.18a.845.845 0 0 0-1.04 1.328l20.812 16.313a.845.845 0 0 0 1.04-1.33l-3.698-2.896c1.392-1.428 2.335-3.027 2.81-4.163a1.118 1.118 0 0 0 0-.864c-.524-1.256-1.625-3.084-3.27-4.61-1.653-1.54-3.927-2.833-6.768-2.833-2.397 0-4.394.925-5.952 2.137L1.364.18Zm6.48 5.076a5.064 5.064 0 0 1 7.857 6.16l-1.357-1.063a3.374 3.374 0 0 0-2.946-4.725c-.204-.007-.324.215-.26.412a2.24 2.24 0 0 1-.116 1.709l-3.175-2.49-.003-.003Zm5.27 8.451A5.064 5.064 0 0 1 6.237 8.29L2.922 5.679c-.802 1.044-1.375 2.084-1.709 2.89a1.118 1.118 0 0 0 0 .864c.524 1.255 1.624 3.084 3.27 4.61 1.652 1.54 3.927 2.833 6.767 2.833 1.68 0 3.16-.454 4.437-1.143l-2.573-2.025Z"
            />
        </svg>
    )
}

export const Weapons: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 18 18" {...svgProps}>
            <path
                fill={tint}
                d="M9 0c.622 0 1.125.503 1.125 1.125v.366a7.597 7.597 0 0 1 6.384 6.384h.366a1.124 1.124 0 1 1 0 2.25h-.366a7.597 7.597 0 0 1-6.384 6.384v.366a1.124 1.124 0 1 1-2.25 0v-.366a7.593 7.593 0 0 1-6.384-6.384h-.366a1.124 1.124 0 1 1 0-2.25h.366a7.593 7.593 0 0 1 6.384-6.384v-.366C7.875.503 8.378 0 9 0ZM3.776 10.125a5.351 5.351 0 0 0 4.099 4.1V13.5a1.124 1.124 0 1 1 2.25 0v.724a5.351 5.351 0 0 0 4.1-4.099H13.5a1.124 1.124 0 1 1 0-2.25h.724a5.346 5.346 0 0 0-4.099-4.1V4.5a1.124 1.124 0 1 1-2.25 0v-.724a5.347 5.347 0 0 0-4.1 4.099H4.5a1.124 1.124 0 1 1 0 2.25h-.724ZM9 7.875a1.125 1.125 0 1 1 0 2.25 1.125 1.125 0 0 1 0-2.25Z"
            />
        </svg>
    )
}

export const Wallet: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 20 20" fill="none" {...svgProps}>
            <path d="M15.8333 5.83333V3.33333C15.8333 3.11232 15.7455 2.90036 15.5893 2.74408C15.433 2.5878 15.221 2.5 15 2.5H4.16667C3.72464 2.5 3.30072 2.67559 2.98816 2.98816C2.67559 3.30072 2.5 3.72464 2.5 4.16667C2.5 4.60869 2.67559 5.03262 2.98816 5.34518C3.30072 5.65774 3.72464 5.83333 4.16667 5.83333H16.6667C16.8877 5.83333 17.0996 5.92113 17.2559 6.07741C17.4122 6.23369 17.5 6.44565 17.5 6.66667V10M17.5 10H15C14.558 10 14.134 10.1756 13.8215 10.4882C13.5089 10.8007 13.3333 11.2246 13.3333 11.6667C13.3333 12.1087 13.5089 12.5326 13.8215 12.8452C14.134 13.1577 14.558 13.3333 15 13.3333H17.5C17.721 13.3333 17.933 13.2455 18.0893 13.0893C18.2455 12.933 18.3333 12.721 18.3333 12.5V10.8333C18.3333 10.6123 18.2455 10.4004 18.0893 10.2441C17.933 10.0878 17.721 10 17.5 10Z" stroke={tint} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M2.5 4.16602V15.8327C2.5 16.2747 2.67559 16.6986 2.98816 17.0112C3.30072 17.3238 3.72464 17.4993 4.16667 17.4993H16.6667C16.8877 17.4993 17.0996 17.4116 17.2559 17.2553C17.4122 17.099 17.5 16.887 17.5 16.666V13.3327" stroke={tint} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    )
}

export const Dollar: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 24 24" fill="none" {...svgProps}>
            <g>
                <path
                    fill={tint}
                    d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4Z"
                />
            </g>
        </svg>
    )
}

export const DollarSquare: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 16 16" fill="none" {...svgProps}>
            <g
                stroke={tint}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.333}
            >
                <path d="M2.667 1.333v13.334L4 14l1.333.667L6.667 14 8 14.667 9.333 14l1.334.667L12 14l1.333.667V1.333L12 2l-1.333-.667L9.333 2 8 1.333 6.667 2l-1.334-.667L4 2l-1.333-.667Z" />
                <path d="M10.667 5.333h-4a1.333 1.333 0 0 0 0 2.667h2.666a1.333 1.333 0 1 1 0 2.667h-4M8 11.667V4.333" />
            </g>
        </svg>
    )
}

export const Split: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 16 16" fill="none" {...svgProps}>
            <g
                stroke={tint}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.333}
                clipPath="url(#a)"
            >
                <path d="M14 8c.368 0 .67-.3.633-.665a6.667 6.667 0 0 0-5.968-5.968c-.367-.036-.666.266-.666.634v5.333A.667.667 0 0 0 8.666 8L14 8Z" />
                <path d="M14.14 10.593a6.666 6.666 0 1 1-8.807-8.706" />
            </g>
        </svg>
    )
}

export const Contactless: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 24 24" fill="none" {...svgProps}>
            <path
                stroke={tint}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 6H3a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h5a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1ZM13 8.32a7.43 7.43 0 0 1 0 7.36M16.46 6.21a11.76 11.76 0 0 1 0 11.58M19.91 4.1a15.911 15.911 0 0 1 .01 15.8"
            />
        </svg>
    )
}

export const Copy: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 20 20" fill="none" {...svgProps}>
            <g
                stroke={tint}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                clipPath="url(#a)"
            >
                <path d="M16.667 6.666H8.334c-.92 0-1.667.746-1.667 1.667v8.333c0 .92.746 1.667 1.667 1.667h8.333c.92 0 1.667-.746 1.667-1.667V8.333c0-.92-.747-1.667-1.667-1.667Z" />
                <path d="M3.334 13.333c-.917 0-1.667-.75-1.667-1.667V3.333c0-.917.75-1.667 1.667-1.667h8.333c.917 0 1.667.75 1.667 1.667" />
            </g>
        </svg>
    )
}

export const Create: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 18 14" {...svgProps}>
            <path
                fill={tint}
                d="M2.625 0A2.626 2.626 0 0 0 0 2.625v8.75A2.626 2.626 0 0 0 2.625 14h12.25a2.626 2.626 0 0 0 2.625-2.625v-8.75A2.626 2.626 0 0 0 14.875 0H2.625ZM1.75 2.625c0-.484.391-.875.875-.875h12.25c.484 0 .875.391.875.875v8.75a.874.874 0 0 1-.875.875H2.625a.874.874 0 0 1-.875-.875v-8.75Zm4.37 2.188a1.313 1.313 0 1 0-2.627 0 1.313 1.313 0 0 0 2.627 0ZM2.625 8.457c0 .402.325.73.73.73H4.89a2.84 2.84 0 0 1 1.783-2.012A1.443 1.443 0 0 0 5.98 7H4.082c-.803 0-1.457.654-1.457 1.457Zm9.986.73h1.534a.73.73 0 0 0 .73-.73c0-.806-.653-1.457-1.457-1.457H11.52c-.252 0-.487.063-.692.175a2.84 2.84 0 0 1 1.783 2.013Zm-2.44-1.285a1.855 1.855 0 0 0-.327-.027H7.656a1.975 1.975 0 0 0-1.969 1.969c0 .363.293.656.657.656h4.812a.655.655 0 0 0 .656-.656 1.975 1.975 0 0 0-1.64-1.941ZM14 4.812a1.313 1.313 0 1 0-2.626.002A1.313 1.313 0 0 0 14 4.812ZM8.75 7a1.75 1.75 0 1 0-.002-3.502A1.75 1.75 0 0 0 8.75 7Z"
            />
        </svg>
    )
}

export const Smile: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 16 16" {...svgProps}>
            <path
                fill={tint}
                d="M8 16c4.415 0 8-3.585 8-8s-3.585-8-8-8-8 3.585-8 8 3.585 8 8 8Zm-2.872-5.828C5.688 10.819 6.644 11.5 8 11.5c1.356 0 2.313-.681 2.872-1.328a.5.5 0 0 1 .706-.05"
            />
        </svg>
    )
}

export const ID: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 24 24" fill="none" {...svgProps}>
            <path
                stroke={tint}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 10h2M16 14h2M6.17 15a3 3 0 0 1 5.66 0"
            />
            <path
                stroke={tint}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 13a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"
            />
            <path
                stroke={tint}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 5H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2Z"
            />
        </svg>
    )
}

export const Update: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 24 24" {...svgProps}>
            <path
                fill={tint}
                d="M21 12c0-4.967-4.033-9-9-9s-9 4.033-9 9 4.033 9 9 9 9-4.033 9-9ZM0 12C0 5.377 5.377 0 12 0s12 5.377 12 12-5.377 12-12 12S0 18.623 0 12Zm12 3.75c2.07 0 3.75-1.68 3.75-3.75 0-2.07-1.68-3.75-3.75-3.75-2.07 0-3.75 1.68-3.75 3.75 0 2.07 1.68 3.75 3.75 3.75Zm0-10.5A6.753 6.753 0 0 1 18.75 12 6.753 6.753 0 0 1 12 18.75 6.753 6.753 0 0 1 5.25 12 6.753 6.753 0 0 1 12 5.25ZM10.5 12a1.5 1.5 0 1 1 3.001.001A1.5 1.5 0 0 1 10.5 12Z"
            />
        </svg>
    )
}

export const Coins: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 14 14" {...svgProps}>
            <path
                fill={tint}
                d="M14 2.188c0 .492-.391.946-1.05 1.312-.796.44-1.982.752-3.344.845a3.92 3.92 0 0 0-.31-.137C8.22 3.757 6.787 3.5 5.25 3.5c-.227 0-.448.005-.67.016L4.55 3.5c-.659-.366-1.05-.82-1.05-1.313C3.5.98 5.852 0 8.75 0S14 .979 14 2.188ZM4.394 4.405c.279-.02.566-.03.856-.03 1.7 0 3.21.336 4.17.859.678.369 1.08.828 1.08 1.329 0 .109-.02.216-.057.32-.126.36-.465.691-.957.97-.003.003-.009.003-.011.006l-.025.013c-.957.53-2.483.875-4.2.875-1.63 0-3.087-.309-4.052-.795a2.606 2.606 0 0 1-.15-.08C.39 7.51 0 7.055 0 6.562 0 5.612 1.46 4.8 3.5 4.502c.287-.041.585-.074.894-.096Zm6.981 2.157c0-.598-.29-1.09-.659-1.46a9.204 9.204 0 0 0 2.084-.56 4.736 4.736 0 0 0 1.2-.697v.967c0 .528-.451 1.015-1.198 1.392-.399.203-.886.375-1.432.506.002-.05.005-.096.005-.145v-.003ZM10.5 9.188c0 .493-.391.947-1.05 1.313l-.15.08c-.963.486-2.42.795-4.05.795-1.717 0-3.243-.345-4.2-.875C.391 10.134 0 9.68 0 9.187V8.22c.342.281.755.51 1.2.697 1.08.451 2.513.708 4.05.708s2.97-.257 4.05-.708a5.52 5.52 0 0 0 .612-.298c.167-.093.323-.197.47-.306.041-.03.08-.063.118-.093v.968Zm.875 0V7.604c.52-.115.998-.26 1.425-.437A4.736 4.736 0 0 0 14 6.47v.968c0 .287-.137.574-.407.844-.446.446-1.23.813-2.223 1.05.002-.046.005-.095.005-.145ZM5.25 12.25c1.537 0 2.97-.257 4.05-.708a4.733 4.733 0 0 0 1.2-.697v.967C10.5 13.023 8.148 14 5.25 14"
            />
        </svg>
    )
}

export const Beta: FC<BaseProps> = (props) => {
    const { tint = defaultTint, ...svgProps } = props

    return (
        <svg {...defaultDimensions} viewBox="0 0 16 18" {...svgProps}>
            <path
                fill={tint}
                d="M10.125 0H4.5a1.124 1.124 0 1 0 0 2.25v4.669c0 .415-.116.826-.334 1.177L.362 14.28A2.441 2.441 0 0 0 2.44 18h10.87a2.441 2.441 0 0 0 2.078-3.72L11.584 8.1a2.252 2.252 0 0 1-.334-1.178V2.25a1.124 1.124 0 1 0 0-2.25h-1.125ZM6.75 6.919V2.25H9v4.669c0 .833.232 1.649.668 2.359l1.213 1.972H4.869l1.213-1.972c.436-.71.668-1.526.668-2.36Z"
            />
        </svg>
    )
}